import type { AuditFields, ISODateString, LoanRecordStatus } from './common';

/**
 * Day-count convention used for daily interest accrual.
 * Per spec §16/§17 (Phase-1-era doc) — this is NOT confirmed LIC behaviour,
 * it is a configurable assumption. Actual/365 fixed is the default until
 * a real LIC interest certificate can be checked against it.
 */
export type DayCountConvention = 'actual_365_fixed' | 'actual_365_25' | 'actual_360';

/**
 * How pre-EMI interest (interest accrued on disbursed-but-EMI-not-yet-
 * started principal) is treated. Not confirmed for this loan yet — kept
 * configurable per spec instruction ("Pre-EMI: support the concept now,
 * but keep the exact treatment configurable/pending until LIC
 * documentation confirms how my actual loan is handled.").
 */
export type PreEmiInterestTreatment = 'pay_as_you_go' | 'capitalize_at_emi_start';

/**
 * EMI rounding rule. Also unconfirmed / configurable.
 */
export type EmiRoundingRule = 'nearest_paisa' | 'nearest_rupee' | 'nearest_ten_rupees';

/**
 * The user-editable WORKING ASSUMPTION for how payments are allocated
 * between interest and principal. LIC HFL's real allocation is not known
 * from the sanction letter, so calculations always use an explicit,
 * editable assumption and flag results as estimates.
 *
 *  - 'unconfirmed' / unset        : AUTOMATIC (default). Regular EMI start
 *      = the saved regularEmiStartDate if present; otherwise, when the loan
 *      is not fully disbursed by its first EMI date, the first payment is
 *      interest-only and regular EMI starts on the next EMI date (else
 *      regular EMI from the first EMI date). See resolveRepaymentArrangement.
 *  - 'interest_only_then_regular' : EMI dates before regularEmiStartDate are
 *      interest-only; regular EMI from it (automatic date if blank).
 *  - 'regular_from_start'         : regular EMI from regularEmiStartDate (or
 *      from the first EMI date if blank); no interest-only phase.
 */
export type RepaymentArrangement = 'unconfirmed' | 'interest_only_then_regular' | 'regular_from_start';

/**
 * Figures REPORTED by the lender (LIC HFL portal / statement) as an
 * observation on a given date. These are informational: the calculation
 * engine never uses them to overwrite a calculated or event-derived
 * figure. The only exception is `contractualMaturityDate`, which (when
 * present) is used as the contractual horizon for the clearly-labelled
 * "assumed regular EMI" scenario (see engine.ts) in preference to a date
 * derived from tenure. Every field is optional; none is ever invented.
 */
export interface LenderReportedFigures {
  /** The date these figures were observed on the lender's portal/statement. */
  asOfDate?: ISODateString;
  /** The instalment amount currently shown as payable (may be an interest-only pre-EMI amount). */
  instalmentAmount?: number;
  /** Outstanding principal as shown by the lender. Informational; reconcile via a Balance Correction event if it differs. */
  outstandingPrincipal?: number;
  /** Contractual loan maturity / last instalment date reported by the lender. */
  contractualMaturityDate?: ISODateString;
  /** Number of unpaid instalments reported by the lender. */
  unpaidInstalments?: number;
  /** The amount the lender reports for the last EMI payable. */
  lastInstalmentAmount?: number;
}

/**
 * The Loan record. There is exactly one of these per app instance (v1
 * scope is single-loan only — see spec: "Single-loan LIC HFL tracker").
 *
 * This record holds:
 *  (a) fixed reference facts about the loan (lender, account number),
 *  (b) the ORIGINAL/BASE terms as first known (sanction amount, original
 *      rate, original tenure) — these fields are the anchor for the
 *      "Baseline A" interest-saved calculation and must never be
 *      overwritten once the loan is no longer in draft status. Changes
 *      to actual terms over time (rate changes, disbursements,
 *      EMI/tenure changes) are captured as EVENTS (see events.ts), not
 *      as edits to this record.
 *  (c) per-loan configuration for the LIC-specific conventions that
 *      cannot currently be verified (day-count, pre-EMI treatment, EMI
 *      rounding) — each is a plain configurable field, clearly optional/
 *      defaulted, never presented as confirmed LIC fact.
 *
 * Status lifecycle: a Loan starts as 'draft_expected' (skeleton terms,
 * before any LIC sanction letter/KFS exists) and can be marked
 * 'lic_confirmed' once the user has entered real sanctioned terms. This
 * flag is purely a display/labelling concern — it does not change how
 * the calculation engine treats the record. The user can keep editing
 * a draft loan's base terms directly (see below) until it's confirmed;
 * once confirmed, base terms should generally no longer be edited
 * directly — legitimate changes from that point are modelled as events.
 */
export interface Loan extends AuditFields {
  /** Always 'loan-primary' in v1 (single-loan scope). Reserved for future multi-loan support. */
  slug: 'loan-primary';

  status: LoanRecordStatus;

  // --- Reference / identity fields ---
  lender: string; // e.g. "LIC Housing Finance Ltd"
  loanAccountNumber?: string; // may be unknown while in draft_expected status

  // --- Original / base terms (the anchor for Baseline A) ---
  /** What LIC sanctioned — a reference ceiling, NEVER used directly as outstanding principal. */
  sanctionAmount: number;
  /**
   * What was actually approved, if different from sanctioned. By
   * default this AUTOMATICALLY tracks sanctionAmount (the common case
   * where they're identical) so the user doesn't have to enter the same
   * figure twice. Once the user manually edits this field to a
   * different value, `isApprovedAmountManuallySet` is set to true and
   * this field stops auto-following sanctionAmount — see the loan setup
   * form for the exact UX (an explicit "reset to match sanctioned
   * amount" action restores the automatic link).
   */
  approvedAmount: number;
  /**
   * Whether approvedAmount has been manually set to a value different
   * from sanctionAmount (true), or is still automatically tracking it
   * (false/undefined). Purely a UI/display concern for the loan setup
   * form — the calculation engine always uses whatever numeric value is
   * stored in approvedAmount regardless of this flag.
   */
  isApprovedAmountManuallySet?: boolean;
  /** The rate as originally sanctioned/expected. Immutable once loan is lic_confirmed. */
  originalAnnualRatePercent: number;
  /** Original tenure in months, as sanctioned/expected. */
  originalTenureMonths: number;
  /**
   * The EMI originally calculated/quoted at full disbursement, before any
   * actual disbursement-driven recalculation. For an under-construction/
   * staged-disbursement loan this is often a projected "eventual EMI"
   * figure, not something charged from day one.
   *
   * By default this is CALCULATED automatically from approvedAmount +
   * originalAnnualRatePercent + originalTenureMonths using the standard
   * reducing-balance EMI formula (see src/lib/calc/emi.ts) — the user
   * does not need to enter it manually unless they have an actual
   * LIC-confirmed EMI that differs from the calculated value. See
   * `isEmiManuallyOverridden` below for which mode is in effect.
   */
  originalEmiAtFullDisbursement: number;

  /**
   * Whether `originalEmiAtFullDisbursement` was manually entered/
   * overridden by the user (true) or is the auto-calculated value from
   * the loan's other base terms (false/undefined). Purely a UI/display
   * concern — the calculation engine (Phase 2+) always uses whatever
   * numeric value is stored, regardless of this flag; it exists so the
   * loan setup form can correctly show "Calculated" vs "Manually
   * Entered" when the record is reopened for editing, and so editing
   * approvedAmount/rate/tenure later knows whether it's safe to
   * silently recalculate EMI or whether the user has taken over.
   */
  isEmiManuallyOverridden?: boolean;

  /**
   * The regular (principal + interest) EMI amount payable once regular
   * repayment begins, IF it is known (communicated by the lender or
   * chosen by the user). DELIBERATELY separate from
   * `originalEmiAtFullDisbursement`, which is the baseline EMI for the
   * FULL sanctioned amount and is never overwritten by this value.
   * Optional: when absent, the engine does not assume the full-loan EMI
   * applies to a partially disbursed loan — see engine.ts
   * ("regular EMI resolution") for the exact, labelled fallback order.
   * Later repayment-consequence events (rate change / prepayment /
   * balance correction) still override this from their own effective date.
   */
  regularEmiAmount?: number;

  /** Day of month EMI is due. Defaults to 7 per the user's stated normal EMI date. */
  emiDayOfMonth: number;

  /**
   * The date regular EMI is expected/known to start. Nullable — for a
   * draft/expected loan this may not be known yet. This is independent
   * from full disbursement (spec §6: "EMI-started" and
   * "disbursement-complete" are two independent flags).
   *
   * HOW IT IS READ (engine contract, see resolveRepaymentArrangement):
   *  - With the AUTOMATIC assumption (repaymentArrangement unset or the
   *    stored value 'unconfirmed') the date keeps the meaning it has always
   *    had here: the first regular EMI is the first EMI date strictly AFTER
   *    it. EMI day 7 and a date of 7 Nov 2026 => the 7 Nov payment is still
   *    interest-only and 7 Dec is the first regular EMI.
   *  - With an explicitly chosen assumption, regular EMI applies FROM the
   *    date: a due date on or after it is a regular instalment.
   * The first regular EMI due date is derived (`firstRegularEmiDueDate`),
   * never stored. When unset, an automatic date is used.
   */
  regularEmiStartDate?: ISODateString;

  /**
   * Loan start date — for an under-construction property this is
   * typically the date of the first disbursement, but is kept as an
   * explicit field since sanction letters usually state one.
   */
  loanStartDate?: ISODateString;

  // --- Configurable LIC-specific conventions (NOT confirmed facts) ---
  dayCountConvention: DayCountConvention;
  preEmiInterestTreatment: PreEmiInterestTreatment;
  emiRoundingRule: EmiRoundingRule;

  /**
   * The chosen repayment working assumption (see RepaymentArrangement).
   * Optional for backward compatibility: when absent the engine uses the
   * automatic working assumption, resolved WITHOUT altering stored data.
   */
  repaymentArrangement?: RepaymentArrangement;

  /** Figures reported by the lender on a given date (informational observations; see LenderReportedFigures). */
  lenderReported?: LenderReportedFigures;

  /**
   * Free-text notes field for anything the user wants to record about
   * the loan that doesn't fit a structured field yet (e.g. "KFS pending,
   * expect sanction in Nov 2026").
   */
  notes?: string;
}

/**
 * Default configuration values for a brand-new draft loan, per the
 * "Assumptions/Defaults" section of the approved spec. These are NOT
 * claims about LIC's actual behaviour — see field-level docs above.
 */
export const DEFAULT_LOAN_CONFIG: Pick<
  Loan,
  'dayCountConvention' | 'preEmiInterestTreatment' | 'emiRoundingRule' | 'emiDayOfMonth'
> = {
  dayCountConvention: 'actual_365_fixed',
  preEmiInterestTreatment: 'pay_as_you_go',
  emiRoundingRule: 'nearest_paisa',
  emiDayOfMonth: 7,
};
